import { z } from 'zod';
import {
  IDENTIFIER_PATTERN,
  MONEY_PATTERN,
  PASSWORD_MAX_LENGTH,
  RENEWAL_CONDITIONS,
  RENEWAL_CURRENCY,
  SERVICE_TYPES,
  isHttpUrl,
  isValidIsoDate,
} from '@bekebi/domain';

/** Uniform error body for every API response. Never contains stack traces or provider text. */
export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/**
 * Login input. The password policy is enforced when passwords are set, not at login,
 * so a login attempt never reveals policy details; only an upper bound is applied here
 * to cap hashing cost.
 */
export const loginRequestSchema = z.object({
  email: z.string().trim().min(1).max(254),
  password: z
    .string()
    .min(1)
    .max(PASSWORD_MAX_LENGTH * 4),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// ---------------------------------------------------------------------------
// Shared field schemas (Phase 02)
// ---------------------------------------------------------------------------

export const uuidSchema = z.string().uuid();

/** Non-blank display name, trimmed. */
export const nameSchema = z.string().trim().min(1).max(200);

/** Absolute http(s) URL (DECISIONS.md A-S3). */
export const httpUrlSchema = z
  .string()
  .trim()
  .refine(isHttpUrl, { message: 'must be an absolute http:// or https:// URL' });

/** Internal identifier format (resource kinds, binding roles). */
export const identifierSchema = z.string().regex(IDENTIFIER_PATTERN);

export const serviceTypeSchema = z.enum(SERVICE_TYPES);

// ---------------------------------------------------------------------------
// Renewal field schemas (Phase 03; DECISIONS.md A-R3–A-R5, A-R10)
// ---------------------------------------------------------------------------

/** A real calendar date, YYYY-MM-DD. */
export const isoDateSchema = z
  .string()
  .refine(isValidIsoDate, { message: 'must be a valid YYYY-MM-DD date' });

/** Money as an exact decimal string with exactly two decimals, e.g. "1500.00". */
export const moneyAmountSchema = z.string().regex(MONEY_PATTERN, 'must have exactly two decimals');

export const priceSchema = z.object({
  amount: z.string(),
  currency: z.literal(RENEWAL_CURRENCY),
});
export type Price = z.infer<typeof priceSchema>;

export const renewalConditionSchema = z.enum(RENEWAL_CONDITIONS);

/** Derived on every read, never stored. */
export const countdownSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('due_today'), value: z.null(), unit: z.null(), label: z.string() }),
  z.object({
    kind: z.literal('remaining'),
    value: z.number().int().positive(),
    unit: z.enum(['day', 'week', 'month']),
    label: z.string(),
  }),
  z.object({
    kind: z.literal('overdue'),
    value: z.number().int().positive(),
    unit: z.literal('day'),
    label: z.string(),
  }),
]);
